import React, { FC, useContext } from "react";
import { Card, CardHeader, CardContent, IconButton, Box } from "@mui/material";
import { defaultCardComponentProps } from "../../constants/globalVariable";
import IconComponent from "../../icons";
import { ThemeContext } from "../../../ProviderApp";
import { THEME_DARK, THEME_LIGHT } from "../../../App.constant";
import { t, untranslated } from "@i18n";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import type { CardComponentProps } from "../../interface";

const CardComponent: FC<CardComponentProps> = (props: CardComponentProps) => {
  /*******************
    @Purpose : Card Component (Material UI)
    @Parameter : CardComponentProps
    @Author : Techture
  *******************/

  // The provider also supplies `themeType`, which `ThemeContext`'s default value does not declare.
  const themeContext = useContext(ThemeContext) as { themeType?: string };

  const getHeaderBackgroundColor = () => {
    if (props.headerBackgroundColor) {
      return props.headerBackgroundColor;
    } else if (themeContext.themeType == THEME_LIGHT) {
      return "rgba(209, 223, 244, 0.5)";
    } else if (themeContext.themeType == THEME_DARK) {
      return "var(--neutral-black)";
    }
  };

  const getContentBackground = () => {
    if (props.background) {
      return props.background;
    } else if (themeContext.themeType == THEME_LIGHT) {
      return "var(--surface-white)";
    } else if (themeContext.themeType == THEME_DARK) {
      return "var(--surface-black)";
    }
  };

  const getTextColor = () => {
    if (themeContext.themeType == THEME_LIGHT) {
      return "var(--primary-text)";
    } else {
      return "var(--surface-neutral)";
    }
  };

  const handleBodyClick = (e: React.SyntheticEvent) => {
    if (props.onClick) {
      props.onClick(e, props.dataBound);
    }
  };

  const headerBackgroundColor = getHeaderBackgroundColor();
  const contentBackground = getContentBackground();
  const textColor = getTextColor();

  return (
    <Card
      className="cube-card"
      component="article"
      aria-labelledby={props.cardHeaderText ? `card-header-${props.cardHeaderText}` : undefined}
      sx={{
        height: "100%",
        width: props.width || "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        background: "linear-gradient(111deg, var(--surface-white) 45%, var(--white-opacity) 129%)",
        backgroundImage: "linear-gradient(132deg, #fff, rgba(255, 255, 255, 0.5) 100%)",
        boxShadow: "var(--size-4) var(--size-4) var(--size-15) var(--size-0) var(--card-box-shadow)",
        borderRadius: "var(--size-8)",
        border: "none !important",
        padding: 0,
        flexBasis: "var(--size-310)",
        flexShrink: 1,
        color: textColor,
        outline: "none !important",
        transition: "none",
        "&:hover": {
          border: "none !important",
          outline: "none !important",
          boxShadow: "var(--size-4) var(--size-4) var(--size-15) var(--size-0) var(--card-box-shadow)",
        },
        "&:focus": {
          outline: "none !important",
        },
        "&:focus-visible": {
          outline: "none !important",
        },
        "& .MuiCardContent-root": {
          "&::-webkit-scrollbar": {
            width: "0.375rem",
            height: "0.375rem",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0, 0, 0, 0.2)",
            borderRadius: "0.1875rem",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "rgba(0, 0, 0, 0.3)",
          },
        },
      }}
      elevation={0}
    >
      {props.cardHeaderText ? (
        <CardHeader
          className="e-card-header"
          sx={{
            background: headerBackgroundColor,
            padding: "0 !important",
            width: "100% !important",
            "& .MuiCardHeader-content": {
              overflow: "hidden",
              width: "100%",
            },
            "& .MuiCardHeader-action": {
              margin: 0,
              alignSelf: "center",
            },
          }}
          title={
            <Box
              className="e-card-header-title"
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontStyle: "normal",
                fontWeight: "bold",
                fontSize: "var(--size-14)",
                lineHeight: "var(--size-18)",
                color: textColor,
                fontFamily: "inherit",
              }}
            >
              <span
                id={props.cardHeaderText ? `card-header-${props.cardHeaderText}` : undefined}
                data-testid={props.cardHeaderText}
              >
                {props.cardHeaderText}
              </span>
              <span className="e-card-header-share" data-testid={`${props.cardHeaderText}-headerShare`}>
                {props.enableShareIcon && (
                  <IconButton
                    onClick={props.onViewCLick}
                    aria-label={`View all ${props.cardHeaderText}`}
                    size="small"
                    sx={{
                      marginInlineStart: "1rem",
                      padding: 0,
                      minWidth: "auto",
                      border: "none !important",
                      outline: "none !important",
                      "&:hover": {
                        backgroundColor: "transparent",
                        border: "none !important",
                        outline: "none !important",
                      },
                      "&:focus": {
                        outline: "0.125rem solid var(--primary-gradient-color1) !important",
                        outlineOffset: "0.125rem",
                        border: "none !important",
                      },
                      "&:focus-visible": {
                        outline: "0.125rem solid var(--primary-gradient-color1) !important",
                        outlineOffset: "0.125rem",
                        border: "none !important",
                      },
                    }}
                  >
                    <IconComponent
                      name="linkout"
                      width="1rem"
                      height="1rem"
                      toolTipContent={t("View all {{name}}", { name: props.cardHeaderText })}
                      position="bottom"
                    />
                  </IconButton>
                )}
              </span>
            </Box>
          }
          action={
            <Box
              className="e-card-header-link"
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontStyle: "normal",
                fontWeight: 700,
                fontSize: "var(--size-14)",
                lineHeight: "var(--size-18)",
                background:
                  "linear-gradient(100.44deg, var(--primary-gradient-color1) 0%, var(--primary-gradient-color2) 110.4%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                paddingInlineEnd: "var(--size-10)",
              }}
              data-testid={`${props.cardHeaderText}-headerLink`}
            >
              <span className="e-card-header-max" style={{ marginInlineEnd: "var(--size-18)" }}>
                {props.enableMaxMinIcon && (
                  <IconButton
                    data-testid={`${props.cardHeaderText}-headerMax`}
                    onClick={props.onMaximizeClick}
                    aria-label={props.maxMinIconName === "maximizeIcon" ? "Maximize" : "Minimize"}
                    size="small"
                    sx={{
                      padding: 0,
                      minWidth: "auto",
                      border: "none !important",
                      outline: "none !important",
                      "&:hover": {
                        backgroundColor: "transparent",
                        border: "none !important",
                        outline: "none !important",
                      },
                      "&:focus": {
                        outline: "0.125rem solid var(--primary-gradient-color1) !important",
                        outlineOffset: "0.125rem",
                        border: "none !important",
                      },
                      "&:focus-visible": {
                        outline: "0.125rem solid var(--primary-gradient-color1) !important",
                        outlineOffset: "0.125rem",
                        border: "none !important",
                      },
                    }}
                  >
                    <IconComponent
                      name={props.maxMinIconName}
                      toolTipContent={
                        // `maxMinIconName` is optional and React 19 ignores the `defaultProps` below,
                        // so it really can be undefined here; erased assertion, guarding would change behaviour.
                        props.maxMinIconName == "maximizeIcon" ? t("Maximise") : untranslated(props.maxMinIconName!)
                      }
                      position="bottom"
                      width="1.5rem"
                      height="1.5rem"
                    />
                  </IconButton>
                )}
              </span>
              <span style={{ alignSelf: "baseline" }}>
                {props.enableThreeDotsIcon && (
                  <SelectDropDown
                    items={props.items}
                    cssClass="e-caret-hide"
                    select={props.selectDropDownItem}
                    bgColor="transparent"
                    data-testid={`${props.cardHeaderText}-threeDots`}
                  >
                    <IconComponent name="threedots" width="1.25rem" height="1.25rem" />
                  </SelectDropDown>
                )}
              </span>
            </Box>
          }
          slotProps={{
            content: {
              sx: {
                display: "flex",
                justifyContent: "space-between",
                padding: "var(--size-11) var(--size-20) var(--size-11) var(--size-20)",
                width: "100%",
              },
            },
          }}
        />
      ) : null}
      <CardContent
        className="e-card-content"
        component={props.onClick ? "button" : "div"}
        onClick={handleBodyClick}
        data-testid="cardContent"
        role={props.onClick ? "button" : undefined}
        tabIndex={props.onClick ? 0 : undefined}
        aria-label={props.onClick ? "Card content - click to interact" : undefined}
        onKeyDown={
          props.onClick
            ? (e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleBodyClick(e);
                }
              }
            : undefined
        }
        sx={{
          display: "flex",
          padding: "var(--size-0)",
          height: "100%",
          alignItems: "flex-start",
          background: props.background || contentBackground,
          color: textColor,
          overflow: "auto",
          position: "relative",
          cursor: props.onClick ? "pointer" : "default",
          border: "none !important",
          outline: "none !important",
          textAlign: "left",
          fontFamily: "inherit",
          transition: "filter 0.2s ease",
          "&:hover": props.onClick
            ? {
                background: props.background || contentBackground,
                filter: "brightness(0.98)",
                border: "none !important",
                outline: "none !important",
              }
            : {
                border: "none !important",
                outline: "none !important",
              },
          "&:focus": props.onClick
            ? {
                outline: "0.125rem solid var(--primary-gradient-color1) !important",
                outlineOffset: "-0.125rem",
                border: "none !important",
              }
            : {
                outline: "none !important",
                border: "none !important",
              },
          "&:focus-visible": props.onClick
            ? {
                outline: "0.125rem solid var(--primary-gradient-color1) !important",
                outlineOffset: "-0.125rem",
                border: "none !important",
              }
            : {
                outline: "none !important",
                border: "none !important",
              },
          "&:last-child": {
            padding: "var(--size-0)",
          },
        }}
      >
        {props.cardBody}
      </CardContent>
    </Card>
  );
};
// React 18.3 (this app's runtime) still applies `defaultProps` on a function component — it only
// warns, and it is React 19 that drops them — but @types/react is already on 19 and no longer
// declares the field, so the defaults are attached as a real property instead of asserted on.
export default Object.assign(CardComponent, { defaultProps: defaultCardComponentProps });
